import type { IncidentRecord, AssessmentPayload } from '../../incident-logging/types/incident.types';

// ── Case Status ─────────────────────────────────────────────────────────────
export type CaseStatus =
  | 'new'
  | 'under_review'
  | 'referred_to_doctor'
  | 'awaiting_clearance'
  | 'cleared_to_play'
  | 'closed';

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  new: 'New',
  under_review: 'Under Review',
  referred_to_doctor: 'Referred to Doctor',
  awaiting_clearance: 'Awaiting Clearance',
  cleared_to_play: 'Cleared to Play',
  closed: 'Closed',
};

export const CASE_STATUSES: CaseStatus[] = [
  'new',
  'under_review',
  'referred_to_doctor',
  'awaiting_clearance',
  'cleared_to_play',
  'closed',
];

// ── Outcome display config ──────────────────────────────────────────────────
export type OutcomeKey = 'emergency' | 'possible_concussion' | 'monitoring' | 'no_signs';

export const OUTCOME_CONFIG: Record<OutcomeKey, { label: string; colorClass: string; borderClass: string; bgClass: string }> = {
  emergency: {
    label: 'Emergency',
    colorClass: 'text-sy-red',
    borderClass: 'border-sy-red',
    bgClass: 'dark:bg-sy-red/10 bg-red-50',
  },
  possible_concussion: {
    label: 'Possible Concussion',
    colorClass: 'dark:text-amber-400 text-amber-700',
    borderClass: 'border-amber-500',
    bgClass: 'dark:bg-amber-500/10 bg-amber-50',
  },
  monitoring: {
    label: 'Monitoring',
    colorClass: 'dark:text-yellow-400 text-yellow-700',
    borderClass: 'border-yellow-500',
    bgClass: 'dark:bg-yellow-500/10 bg-yellow-50',
  },
  no_signs: {
    label: 'No Signs',
    colorClass: 'dark:text-sy-green text-green-700',
    borderClass: 'border-sy-green',
    bgClass: 'dark:bg-sy-green/10 bg-green-50',
  },
};

// ── Notes ───────────────────────────────────────────────────────────────────
export type IncidentNote = {
  id: string;
  incident_id: string;
  author_id: string;
  note_text: string;
  created_at: string;
  author_name?: string;
};

// ── Status History ──────────────────────────────────────────────────────────
export type StatusHistoryEntry = {
  id: string;
  incident_id: string;
  status: CaseStatus;
  changed_by: string;
  note: string | null;
  changed_at: string;
  changed_by_name?: string;
};

// ── Extended incident with status + notes ───────────────────────────────────
export type IncidentWithDetails = IncidentRecord & {
  current_status: CaseStatus;
  notes: IncidentNote[];
  status_history: StatusHistoryEntry[];
};

// ── Filter state ────────────────────────────────────────────────────────────
export type DatePreset = 'today' | 'this_week' | 'this_month' | 'all';

export type AssessmentFilters = {
  search: string;
  outcome: OutcomeKey | 'all';
  status: CaseStatus | 'all';
  datePreset: DatePreset;
  dateFrom: string | null;
  dateTo: string | null;
  venueId: string | null;
};

// ── API response ────────────────────────────────────────────────────────────
export type PaginatedResponse<T> = {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
};
